import { useState } from "react";
import { CaretDown, CaretRight, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MatchingGiftAskScript
 *
 * Collapsible "Sample ask script" panel that appears under the Employee Gift
 * Match block on each Corporate Scout card. Renders a ready-to-send 2–3 line
 * pitch the nonprofit can copy/paste into an email, DM, or social post to ask
 * one of the company's employees to submit a matching-gift request after
 * making a donation.
 *
 * Pure JS template — no LLM cost, no backend call. The script is anchored to:
 *   - the user's nonprofit org name (from their NonprofitProfile)
 *   - the company name + match ratio + match cap (from the verified scout data)
 *   - a sensible default ask amount the user can easily edit
 */

const buildScript = ({ orgName, companyName, ratio, matchMax, programUrl }) => {
  const ratioPhrase = (ratio || "1:1").replace(":", " to ");
  const ratioWords = ratio === "2:1" ? "double" : ratio === "3:1" ? "triple" : "double";
  const capLine = matchMax
    ? `${companyName} matches employee gifts to 501(c)(3) nonprofits ${ratioPhrase} — up to ${matchMax}.`
    : `${companyName} matches employee gifts to 501(c)(3) nonprofits ${ratioPhrase}.`;
  const portalLine = programUrl
    ? `Their matching-gift portal is here: ${programUrl}`
    : `Their matching-gift portal is in their internal HR system — search "matching gifts" on their intranet, or check Benevity/CyberGrants/YourCause if those names ring a bell.`;

  return `Hi [DONOR FIRST NAME],

Thank you so much for your $[AMOUNT] gift to ${orgName} — it's already going to work.

One quick favor that costs you nothing: ${capLine} If you submit a matching-gift request through ${companyName}, your $[AMOUNT] will ${ratioWords} for our work — and it takes about 2 minutes.

${portalLine}

You'll need our 501(c)(3) info: ${orgName}, EIN [INSERT EIN]. Most submissions are auto-verified.

Thank you again — this kind of small step is how we go from "thanks" to real impact.

[YOUR NAME]
${orgName}`;
};

const MatchingGiftAskScript = ({ scout, orgName }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const safeOrgName = orgName?.trim() || "[Your Nonprofit Name]";
  const script = buildScript({
    orgName: safeOrgName,
    companyName: scout.company_name,
    ratio: scout.match_ratio || "1:1",
    matchMax: scout.match_max,
    programUrl: scout.match_program_url,
  });

  const copy = () => {
    navigator.clipboard.writeText(script);
    setCopied(true);
    toast.success("Script copied — paste into email or text");
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="mt-2" data-testid={`match-script-${scout.company_id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-semibold text-lime-300 hover:text-lime-200 transition-colors"
        data-testid={`match-script-toggle-${scout.company_id}`}
        aria-expanded={open}
      >
        {open ? (
          <CaretDown weight="bold" className="w-3 h-3" />
        ) : (
          <CaretRight weight="bold" className="w-3 h-3" />
        )}
        {open ? "Hide sample ask script" : "Show sample ask script you can copy/paste"}
      </button>

      {open && (
        <div className="mt-2 bg-zinc-950 border border-lime-500/30 rounded-sm p-3" data-testid={`match-script-body-${scout.company_id}`}>
          <p className="text-[10px] uppercase tracking-wider text-lime-400 font-mono mb-2">
            For an employee/donor at {scout.company_name}
          </p>
          <pre
            className="text-xs text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed"
            data-testid={`match-script-text-${scout.company_id}`}
          >
            {script}
          </pre>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-lime-500/15 hover:bg-lime-500/25 border border-lime-500/40 text-lime-200 px-3 py-1.5 rounded-sm transition-colors"
              data-testid={`match-script-copy-${scout.company_id}`}
            >
              {copied ? (
                <>
                  <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy script
                </>
              )}
            </button>
            <span className="text-[11px] text-zinc-500">
              Replace [DONOR FIRST NAME], [AMOUNT], [INSERT EIN], and [YOUR NAME] before sending.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchingGiftAskScript;
